import React, { useEffect, useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Sheet from '@amzn/meridian/sheet';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import documentTokens from '@amzn/meridian-tokens/base/icon/document';
import { colorBlue50, colorBlue600 } from '@amzn/meridian-tokens/base/color';
import { useAuth } from '../auth/AuthContext';
import AppShell, { AppTab } from '../components/AppShell';
import { getAnalyticsService } from '../services';
import { areasFor } from '../services/types';
import type { AppArea } from '../services/types';
import IntegrationGuide from './IntegrationGuide';
import AsnScreen from './AsnScreen';
import PromisedDateScreen from './PromisedDateScreen';
import LeadTimeScreen from './LeadTimeScreen';
import SupplierControlScreen from './SupplierControlScreen';
import SupplierInsightsScreen from './SupplierInsightsScreen';

const AREA_VALUES: AppArea[] = ['connect', 'control', 'insights'];

/** The area named in the URL hash (e.g. "#control"), if valid; else undefined. */
function areaFromHash(): AppArea | undefined {
    const h = window.location.hash.replace(/^#/, '');
    return (AREA_VALUES as string[]).includes(h) ? (h as AppArea) : undefined;
}

export default function LandingScreen() {
    const { user, signOut } = useAuth();
    const areas = useMemo(
        () => areasFor(user?.roles, user?.isEmployee),
        [user?.roles, user?.isEmployee],
    );

    // Hooks must run unconditionally and in a stable order, so declare them
    // before any early return. `areas` may be empty (denied employee); guard the
    // initial-area access so we don't read areas[0] of an empty list.
    // Initialize from the URL hash so a reload restores the open area (e.g.
    // "#control"); fall back to the first area the user can access.
    const [area, setArea] = useState<AppArea | undefined>(() => areaFromHash() ?? areas[0]);
    const [tab, setTab] = useState<AppTab>('asn');
    const [guideOpen, setGuideOpen] = useState(false);

    // Keep the URL hash in sync with the resolved active area so a reload
    // restores it. Runs unconditionally (hook order) and only when the user has
    // areas; the denied-employee case (no areas) leaves the hash untouched.
    const resolvedArea = area && areas.includes(area) ? area : areas[0];
    useEffect(() => {
        if (!resolvedArea) return;
        const next = `#${resolvedArea}`;
        if (window.location.hash !== next) {
            window.history.replaceState(null, '', next);
        }
    }, [resolvedArea]);

    // A federated employee in none of the role groups resolves to no areas.
    // Show an access-denied screen instead of falling through to Supplier
    // Gateway (which is the native-supplier default).
    if (areas.length === 0) {
        return (
            <AppShell userEmail={user?.email} userPicture={user?.picture} onSignOut={signOut}>
                <Column
                    alignmentHorizontal="center"
                    alignmentVertical="center"
                    spacing="300"
                    spacingInset="600"
                    width="100%"
                >
                    <Text type="h500">Access denied</Text>
                    <Text type="b300" color="secondary" alignment="center">
                        Your account isn&apos;t assigned to any Supplier Gateway group. Please reach
                        out to an administrator to request access.
                    </Text>
                </Column>
            </AppShell>
        );
    }

    const activeArea = resolvedArea;

    // "Home" = the user's first/default area with the default Connect tab, and
    // the integration guide closed. Clicking the brand/title in the masthead
    // returns here. (The hash-sync effect updates the URL to the first area.)
    const goHome = () => {
        setArea(areas[0]);
        setTab('asn');
        setGuideOpen(false);
    };

    // Connect area: masthead tab row + integration guide. Area switching moves
    // to the hamburger/left-nav drawer owned by AppShell.
    if (activeArea === 'connect') {
        const navActions = (
            <Button
                type="tertiary"
                size="small"
                onClick={() => {
                    getAnalyticsService().track('integration_guide_opened');
                    setGuideOpen(true);
                }}
            >
                Integration guide
            </Button>
        );
        return (
            <AppShell
                userEmail={user?.email}
                userPicture={user?.picture}
                onSignOut={signOut}
                navActions={navActions}
                tab={tab}
                onTabChange={setTab}
                areas={areas}
                activeArea={activeArea}
                onAreaChange={setArea}
                onHome={goHome}
            >
                <Column spacing="400" width="100%">
                    {tab === 'asn' ? <AsnScreen /> : null}
                    {tab === 'promised' ? <PromisedDateScreen /> : null}
                    {tab === 'leadtime' ? <LeadTimeScreen /> : null}
                </Column>

                <Sheet
                    type="overlay"
                    side="end"
                    open={guideOpen}
                    onClose={() => setGuideOpen(false)}
                    spacingInset="none"
                >
                    <Column spacing="none" width="100%" minWidth={360} maxWidth={640}>
                        {/* Branded header band: icon chip + title + subtitle. */}
                        <Box spacingInset="500" backgroundColor={colorBlue50}>
                            <Row alignmentHorizontal="justify" alignmentVertical="top" spacing="300">
                                <Row spacing="300" alignmentVertical="center">
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 40,
                                            height: 40,
                                            borderRadius: 10,
                                            background: colorBlue600,
                                            color: '#fff',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Icon tokens={documentTokens}>Guide</Icon>
                                    </span>
                                    <Column spacing="100">
                                        <Text type="h400">Integration guide</Text>
                                        <Text type="b100" color="secondary">
                                            Connect your systems to submit ASNs programmatically.
                                        </Text>
                                    </Column>
                                </Row>
                                <Button
                                    type="icon"
                                    label="Close integration guide"
                                    onClick={() => setGuideOpen(false)}
                                >
                                    <Icon tokens={closeTokens}>Close</Icon>
                                </Button>
                            </Row>
                        </Box>
                        <Box spacingInset="500">
                            <IntegrationGuide />
                        </Box>
                    </Column>
                </Sheet>
            </AppShell>
        );
    }

    // Internal areas: no tab row; area switching via the hamburger/left nav.
    return (
        <AppShell
            userEmail={user?.email}
            userPicture={user?.picture}
            onSignOut={signOut}
            areas={areas}
            activeArea={activeArea}
            onAreaChange={setArea}
            onHome={goHome}
        >
            {activeArea === 'control' ? <SupplierControlScreen /> : <SupplierInsightsScreen />}
        </AppShell>
    );
}
